// Copyright (c) Microsoft. All rights reserved.

import {
    Badge,
    Caption1,
    Card,
    CardHeader,
    makeStyles,
    shorthands,
    Text,
    ToggleButton,
} from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronUp20Regular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { IChatMessage } from '../../../libs/models/ChatMessage';
import { customTokens } from '../../../styles';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        ...shorthands.gap(customTokens.spacingVerticalS),
        flexDirection: 'column',
    },
    card: {
        display: 'flex',
        width: '100%',
        height: 'fit-content',
    },
});

interface ICitationCardsProps {
    message: IChatMessage;
}

export const CitationCards: React.FC<ICitationCardsProps> = ({ message }) => {
    const classes = useClasses();

    // Deduplicate citations by link URL
    const uniqueCitations = React.useMemo(() => {
        if (!message.citations || message.citations.length === 0) {
            return [];
        }
        const seenLinks = new Set<string>();
        return message.citations.filter((citation) => {
            if (seenLinks.has(citation.link)) {
                return false;
            }
            seenLinks.add(citation.link);
            return true;
        });
    }, [message.citations]);

    const [showSnippetStates, setShowSnippetStates] = useState<boolean[]>(
        uniqueCitations.map(() => false)
    );

    // Early return if no citations (after all hooks have been called)
    if (uniqueCitations.length === 0) {
        return null;
    }

    const showSnippet = (index: number) => {
        const newShowSnippetStates = [...showSnippetStates];
        newShowSnippetStates[index] = !newShowSnippetStates[index];
        setShowSnippetStates(newShowSnippetStates);
    };

    return (
        <div className={classes.root}>
            {uniqueCitations.map((citation, index) => {
                return (
                    <Card className={classes.card} size="small" key={`citation-card-${index}`}>
                        <CardHeader
                            image={
                                <Badge shape="rounded" appearance="outline" color="informative">
                                    {index + 1}
                                </Badge>
                            }
                            header={
                                citation.link && citation.sourceContentType === 'Web Page' ? (
                                    <a 
                                        href={citation.link} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                    >
                                        <Text weight="semibold" style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                                            {citation.sourceName}
                                        </Text>
                                    </a>
                                ) : (
                                    <Text weight="semibold">{citation.sourceName}</Text>
                                )
                            }
                            description={
                                citation.sourceContentType !== 'Web Page' ? (
                                    <Caption1>Relevance score: {citation.relevanceScore.toFixed(3)}</Caption1>
                                ) : undefined
                            }
                            action={
                                citation.sourceContentType !== 'Web Page' ? (
                                    <ToggleButton
                                        appearance="transparent"
                                        icon={showSnippetStates[index] ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                                        onClick={() => {
                                            showSnippet(index);
                                        }}
                                    />
                                ) : undefined
                            }
                        />

                        {showSnippetStates[index] && citation.sourceContentType !== 'Web Page' && <p>{citation.snippet}</p>}
                    </Card>
                );
            })}
        </div>
    );
};
